import { Directive, Input, TemplateRef, ViewContainerRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { AuthorizationUtility } from './auth.utility';
import { AllowListAuthStore } from './allow-list-auth.store';

@Directive({
  standalone: true,
  selector: '[authorize]',
})
export class AuthorizeDirective implements OnDestroy {
  private hasView = false;
  private id!: string;
  private pageId!: string;
  private tabId!: string;
  private lookup!: string;
  private action!: string;
  private configSub: Subscription;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authUtility: AuthorizationUtility,
    private authStore: AllowListAuthStore
  ) {
    // Re-evaluate whenever auth config reloads (skip the initial BehaviorSubject emission)
    this.configSub = this.authStore.loaded$.pipe(skip(1)).subscribe(() => {
      this.handleView();
    });
  }

  ngOnDestroy(): void {
    this.configSub.unsubscribe();
  }

  @Input() set authorize(config: string[]) {
    this.pageId = config[0];
    this.tabId = config[1];
    this.id = config[2];
    this.lookup = config[3];
    this.action = config[4];
    this.handleView();
  }

  private handleView() {
    if (!this.id || !this.lookup) {
      this.clearView();
      return;
    }

    // No page context — no auth restrictions, always show
    if (!this.pageId) {
      if (!this.hasView) {
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.hasView = true;
      }
      return;
    }

    let hasAccess = false;
    switch (this.lookup) {
      case 'section':
        hasAccess = this.getSectionAccess(this.action ?? 'read');
        break;
      case 'button':
        hasAccess = this.getButtonAccess();
        break;
    }

    if (hasAccess && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAccess && this.hasView) {
      this.clearView();
    }
  }

  private clearView(): void {
    if (this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private getSectionAccess(action: string) {
    switch (action) {
      case 'read':
        return this.authUtility.isSectionVisible(this.pageId, this.tabId, this.id);
      case 'edit':
        return this.authUtility.isSectionEditable(this.pageId, this.tabId, this.id);
      case 'delete':
        return this.authUtility.isSectionDeletable(this.pageId, this.tabId, this.id);
    }
    return false;
  }

  private getButtonAccess() {
    return this.authUtility.isButtonVisible(this.pageId, this.id);
  }
}
